"use client";
/**
 * Challenges page (Block 5, design 1.6) + detail bottom sheet.
 *
 * Lists active + completed challenges with progress bars and reward chips. The
 * whole surface is hidden by routing through `useChallenges().challengesEnabled`
 * — when challenges are disabled (or the endpoint 404s) we render a graceful
 * "not available" state and the Home widget / Profile row are also hidden.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import {
  BottomSheet,
  IconChip,
  ProgressBar,
  SignedOutGate,
  describeReward,
} from "@/components/loyalty/LoyaltyBits";
import { useChallenges, localizedField } from "@/hooks/useLoyaltyExpansion";
import type { CustomerChallenge } from "@/api/services";
import { useAuth } from "@/store/auth";

function challengeProgress(c: CustomerChallenge): { value: number; target: number } {
  const target = c.target ?? c.criteria?.target ?? 0;
  const value = c.progress ?? 0;
  return { value, target };
}

function ChallengeCard({
  challenge,
  completed,
  onOpen,
  t,
  lang,
}: {
  challenge: CustomerChallenge;
  completed: boolean;
  onOpen: () => void;
  t: TFunction;
  lang: string;
}) {
  const name = localizedField(challenge.name, lang) ?? t("v2.challenges.untitled");
  const desc = localizedField(challenge.description, lang);
  const reward = describeReward(challenge.reward, t);
  const { value, target } = challengeProgress(challenge);
  const pct = target > 0 ? value / target : 0;

  return (
    <Card
      padding={14}
      onClick={onOpen}
      style={{
        position: "relative",
        background: completed
          ? "linear-gradient(135deg, #e7f7ee 0%, var(--surface-1) 60%)"
          : "var(--surface-1)",
        border: completed ? "1px solid var(--primary-200)" : "1px solid transparent",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <IconChip tone={completed ? "primary" : "gold"}>
          <Icons.trophy size={20} />
        </IconChip>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="display" style={{ fontSize: 15 }}>
            {name}
          </div>
          {desc && (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {desc}
            </div>
          )}

          {!completed && target > 0 && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  marginBottom: 6,
                }}
              >
                <span className="muted">{t("v2.challenges.progress")}</span>
                <span className="num" style={{ fontWeight: 700 }}>
                  {t("v2.challenges.x_of_y", { x: value, y: target })}
                </span>
              </div>
              <ProgressBar value={pct} />
            </div>
          )}

          {completed && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: "var(--primary-700)",
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              <Icons.check size={14} /> {t("v2.challenges.completed")}
            </div>
          )}

          {reward && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: "1px dashed var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Icons.gift size={14} style={{ color: "var(--bonus)" }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-1)" }}>
                  {reward}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export function ChallengesScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();
  const { loading, error, challengesEnabled, active, completed, unavailable, refresh } =
    useChallenges();
  const [selected, setSelected] = useState<CustomerChallenge | null>(null);

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.challenges.title")} onBack={() => navigate("/profile")} />
        <SignedOutGate
          icon={<Icons.trophy size={32} />}
          title={t("v2.challenges.signin_title")}
          desc={t("v2.challenges.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  // Feature disabled / not deployed → graceful empty.
  if (!loading && (unavailable || !challengesEnabled)) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.challenges.title")} onBack={() => navigate("/profile")} />
        <div style={{ padding: "42px 24px", textAlign: "center", color: "var(--ink-2)" }}>
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-3)",
              margin: "0 auto 14px",
            }}
          >
            <Icons.trophy size={32} />
          </div>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.challenges.unavailable_title")}
          </div>
          <div style={{ maxWidth: 280, margin: "8px auto 0", fontSize: 13, lineHeight: 1.45 }}>
            {t("v2.challenges.unavailable_desc")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.challenges.title")}
        onBack={() => navigate("/profile")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Summary */}
        <Card
          padding={16}
          style={{
            background: "linear-gradient(135deg, var(--primary-50), var(--surface-1))",
            border: "1px solid var(--primary-100)",
            boxShadow: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <IconChip tone="primary">
              <Icons.trophy size={22} />
            </IconChip>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 17 }}>
                {t("v2.challenges.summary", {
                  active: active.length,
                  completed: completed.length,
                })}
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                {t("v2.challenges.summary_sub")}
              </div>
            </div>
          </div>
        </Card>

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
          </Card>
        )}

        {/* Active */}
        {active.length > 0 && (
          <section>
            <div
              className="display"
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ink-2)",
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {t("v2.challenges.active")}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {active.map((c, i) => (
                <ChallengeCard
                  key={c.id ?? c.challengeId ?? i}
                  challenge={c}
                  completed={false}
                  onOpen={() => setSelected(c)}
                  t={t}
                  lang={lang}
                />
              ))}
            </div>
          </section>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <section>
            <div
              className="display"
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ink-2)",
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {t("v2.challenges.completed_section", { count: completed.length })}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {completed.map((c, i) => (
                <ChallengeCard
                  key={c.id ?? c.challengeId ?? i}
                  challenge={c}
                  completed
                  onOpen={() => setSelected(c)}
                  t={t}
                  lang={lang}
                />
              ))}
            </div>
          </section>
        )}

        {!loading && active.length === 0 && completed.length === 0 && (
          <div style={{ padding: "32px 16px", textAlign: "center" }}>
            <div className="muted" style={{ fontSize: 13 }}>
              {t("v2.challenges.empty")}
            </div>
          </div>
        )}
      </div>

      <ChallengeDetailSheet
        challenge={selected}
        onClose={() => setSelected(null)}
        t={t}
        lang={lang}
        onOrder={() => navigate("/")}
      />
    </div>
  );
}

function ChallengeDetailSheet({
  challenge,
  onClose,
  t,
  lang,
  onOrder,
}: {
  challenge: CustomerChallenge | null;
  onClose: () => void;
  t: TFunction;
  lang: string;
  onOrder: () => void;
}) {
  const name = challenge ? localizedField(challenge.name, lang) : null;
  const desc = challenge ? localizedField(challenge.description, lang) : null;
  const reward = challenge ? describeReward(challenge.reward, t) : null;
  const { value, target } = challenge
    ? challengeProgress(challenge)
    : { value: 0, target: 0 };
  const pct = target > 0 ? value / target : 0;
  const isDone = !!challenge?.completedAt || (target > 0 && value >= target);

  return (
    <BottomSheet open={!!challenge} onClose={onClose} label={name ?? undefined}>
      {challenge && (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <IconChip tone="primary" size={52} radius={14}>
              <Icons.trophy size={26} />
            </IconChip>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 19 }}>
                {name ?? t("v2.challenges.untitled")}
              </div>
              {desc && (
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                  {desc}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label={t("v2.common.cancel")}
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--ink-2)",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Icons.close size={16} />
            </button>
          </div>

          {target > 0 && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  marginBottom: 6,
                }}
              >
                <span className="muted">{t("v2.challenges.progress")}</span>
                <span className="num" style={{ fontWeight: 700 }}>
                  {t("v2.challenges.x_of_y", { x: value, y: target })}
                </span>
              </div>
              <ProgressBar value={pct} height={10} />
            </div>
          )}

          {reward && (
            <Card
              padding={12}
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "var(--surface-2)",
                boxShadow: "none",
              }}
            >
              <IconChip tone="amber" size={36}>
                <Icons.gift size={18} />
              </IconChip>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--bonus)" }}>
                  {t("v2.challenges.reward")}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 1 }}>{reward}</div>
              </div>
              {isDone && <span className="badge primary">{t("v2.challenges.auto_sent")}</span>}
            </Card>
          )}

          {!isDone && (
            <Button full onClick={onOrder} style={{ marginTop: 16 }}>
              {t("v2.loyalty_hub.order_now")}
            </Button>
          )}
        </>
      )}
    </BottomSheet>
  );
}
